import { createReducer, on, Action } from '@ngrx/store';
import { HelpActions } from './help.actions';
import { HelpState } from './help.state';
import { isWalkthrougDocumentForUiPath } from '../utils';

const reducer = createReducer(
  HelpState.initialState,
  on(HelpActions.toggleEditor, (state, payload) => ({
    ...state,
    editor: { show: payload.state, document: payload.document },
  })),
  on(HelpActions.toggleTooltips, (state, payload) => ({
    ...state,
    showTooltips: payload.state,
  })),
  on(HelpActions.loadedDocuments, (state, { docs }) => ({
    ...state,
    loadedDocuments: {
      // Convert the loaded documents (an array), to a map with the path as a key.
      // There could be multiple docs per element, so an array is used.
      // Before adding a loaded document into the array, ensure it is not already there
      ...(Array.isArray(docs) ? docs : [])
        // A fetch can resolve AFTER a navigation's deregister prune (the
        // register/deregister buffers flush before the HTTP round-trip
        // completes), so an unfiltered merge re-added the PREVIOUS page's
        // documents and the Walkthroughs menu accumulated other pages' tours
        // until a full browser reload. Keep only docs belonging to a currently
        // registered ui-path; empty-path docs are global/reusable and always
        // apply.
        .filter(
          (d) =>
            !d.path ||
            state.uiPaths.some(
              (p) => p && isWalkthrougDocumentForUiPath(p, d.path)
            )
        )
        .reduce(
          (map, current) => {
            const idx = (map[current.path] || []).findIndex(
              (doc) => current.id === doc.id
            );
            if (idx !== -1) {
              map[current.path] = [...map[current.path]]; //clone the array
              map[current.path][idx] = current; // update the in-memory value
            } else {
              map[current.path] = [...(map[current.path] || []), current]; // doc not in array, or array is null.
            }
            return map;
          },
          { ...state.loadedDocuments } // start with a copy of the currently loaded doc's
        ),
    },
  })),
  on(HelpActions.registerUIPath, (state, { path }) => ({
    ...state,
    uiPaths: [...state.uiPaths, path],
  })),
  on(HelpActions.bulkRegisterUIPaths, (state, { paths }) => ({
    ...state,
    uiPaths: [...state.uiPaths, ...paths],
  })),
  on(HelpActions.bulkDeregisterUIPaths, (state, { paths }) => {
    // Multiset subtract: each deregister removes ONE registration of its path.
    // registerUIPath appends one entry per anchor instance, so removing ALL
    // occurrences here wiped fresh re-registrations whenever a quick A->B->A
    // revisit's buffered deregister flushed after page A re-registered,
    // leaving the revisited page with no paths (and no documents) until a
    // full reload.
    const toRemove = [...paths];
    const newPaths = state.uiPaths.filter((x) => {
      const i = toRemove.indexOf(x);
      if (i !== -1) {
        toRemove.splice(i, 1);
        return false;
      }
      return true;
    });
    return {
      ...state,
      uiPaths: newPaths,
      loadedDocuments: Object.fromEntries(
        Object.entries(state.loadedDocuments).filter(
          // Empty-path docs are global/reusable and survive every prune.
          ([path]) =>
            !path ||
            newPaths.some((p) => p && isWalkthrougDocumentForUiPath(p, path))
        )
      ),
    };
  }),
  on(HelpActions.loadWalkthroughSuccess, (state, { document }) => ({
    ...state,
    playingWalkthrough: document,
    // Reset the step so a freshly loaded walkthrough opens at its intro (Begin)
    // dialog. Without this, re-launching a walkthrough in the same session
    // resumes at the prior tour's stale step index (and a stale value of 0 even
    // made the Begin click a no-op against the distinct step selector). The
    // cross-page resume effect only fires on router navigation, never on load,
    // so this does not disturb mid-tour resume.
    currentStep: null,
  })),
  on(HelpActions.playWalkthrough, (state, { document }) => ({
    ...state,
    playingWalkthrough: document,
    currentStep: 0,
  })),
  on(HelpActions.closeWalkthrough, (state) => ({
    ...state,
    currentStep: null,
    playingWalkthrough: null,
  })),
  on(HelpActions.goToWalkthroughStep, (state, { step }) => ({
    ...state,
    currentStep: step,
  })),
  on(HelpActions.showHelpDeskModal, (state, action) => ({
    ...state,
    helpdesk: {
      showModal: true,
      submission: action,
    },
  })),
  on(HelpActions.setUrlPath, (state, { path }) => ({
    ...state,
    urlPath: path,
  }))
);

export function helpReducer(
  state: HelpState.IState | undefined,
  action: Action
) {
  return reducer(state, action);
}
